import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { ReportRequest } from '../../types/report/ReportTypes';
import ReportExportService from '../../services/report/ReportExportService';

import {
  setReportCode,
  setFilters,
  resetReportExportState,
} from '../../store/slices/report/reportExportSlice';

import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useApiErrorHandler from '../useApiErrorHandler';

/**
 * Hook para gestionar la exportación dinámica de reportes.
 * Mantiene el mismo patrón de uso que useSale.
 */
export const useReportExport = () => {
  const dispatch: AppDispatch = useDispatch();

  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  const { reportCode, filters } = useSelector(
    (state: RootState) => state.reportExport
  );

  // =========================
  // 🔧 State setters
  // =========================

  /**
   * Establece el código del reporte seleccionado.
   */
  const handlerSetReportCode = (code: string) => {
    dispatch(setReportCode(code));
  };

  /**
   * Actualiza los filtros dinámicos del reporte.
   */
  const handlerSetFilters = (newFilters: Record<string, any>) => {
    dispatch(setFilters(newFilters));
  };

  /**
   * Resetea completamente el estado de exportación.
   */
  const handlerResetExportState = () => {
    dispatch(resetReportExportState());
  };

  // =========================
  // 📤 Exportación
  // =========================

  /**
   * Ejecuta la exportación del reporte.
   *
   * 🔄 Muestra loader global
   * ❌ Maneja errores con handler global
   * ✅ Notifica éxito al usuario
   */
  const handlerExportReport = async () => {
    try {
      if (!reportCode) {
        showAlert('warning', 'Reporte requerido', 'Seleccione un tipo de reporte.');
        return;
      }

      showLoadingIndicator();

      const request: ReportRequest = {
        reportCode,
        filters,
      };

      await ReportExportService.exportReport(request);

      showAlert(
        'success',
        'Exportación exitosa',
        'El reporte fue exportado correctamente.'
      );
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  // =========================
  // 🧩 API pública del hook
  // =========================

  return {
    // state
    reportCode,
    filters,

    // setters
    handlerSetReportCode,
    handlerSetFilters,
    handlerResetExportState,

    // actions
    handlerExportReport,
  };
};
